import { clearOf, separate } from "./untangle";

// A row of latch needles knitting plain jersey, one course per pass of the carriage, in 3D. One
// needle pitch is one unit: x runs along the bed, y up the needles and z away from the needles, out
// through the fabric, with the verge, where the sinkers hold the fabric down, at y = 0.
//
// The needles lie in a bed that slopes down and back from the verge, so they rise into the gap
// diagonally, up and toward the fabric, while the fabric hangs straight down in front of the bed.
// Every yarn keeps clear of every other yarn and of the needles. A needle is a stem with a hook
// curled back from its top over a throat, and a latch pivoted on the back of the stem below the hook:
// open, it lies along the stem; shut, it reaches the hook's tip. The yarn runs through the throats,
// in the plane of the hooks' curls, so it can pass a needle anywhere below its hook. Each needle
// pulls its new loop through the old one toward the bed, so the fabric's face, where its legs lie,
// is toward the needles, and its heads and sinker loops lie beyond the legs; a loop on a needle sits
// in the throat, or wraps the back of the stem, its legs coming forward round the stem and down into
// the fabric.
//
// As the carriage passes, each needle in turn:
//   rises, so the loop it holds slides down the stem and pushes the latch open;
//   takes the yarn, which the carrier feeds across its open mouth, as its hook comes down on it;
//   drops, so the old loop rides back up the stem, flips the latch shut over the yarn and slides up
//   the shut latch and round the back of the hook;
//   and drops on, so the old loop comes off over the top and the hook pulls the yarn down through
//   it: the new loop, hanging from the sinkers either side through the old loop into the hook,
//   which holds it until the next pass.
// The old loop then turns over into the fabric round the new loop's legs, its head coming over the
// top of them, as the needle rises back and the takedown draws that wale down a course; the new
// loop's sinker loops go forward over the old loops and down in front of their legs.

export const NEEDLES = 5;

export const PASS_SECONDS = 12;

// The steps follow the middle needle.
export const FEATURED = 2;

// Sizes, in needle pitches: the yarn, the stem, the hook's wire and the latch.
export const YARN_RADIUS = 0.05;
export const STEM_RADIUS = 0.04;
export const HOOK_WIRE = 0.03;
export const LATCH_RADIUS = 0.025;
// The hook's curl, to the middle of its wire; its tip hangs this far below the curl.
export const HOOK_RADIUS = 0.13;
const TIP = 0.05;
// How much of a needle shows below its hook.
const STEM_LENGTH = 1.7;
// The latch's pivot, below the hook's top on the back of the stem; shut, the latch reaches the end
// of the hook's tip, and open it hangs just off the stem.
const PIVOT_DROP = 0.45;
const PIVOT_Z = -(STEM_RADIUS + LATCH_RADIUS);
const SHUT_RISE = PIVOT_DROP - HOOK_RADIUS - TIP;
const SHUT_BACK = 2 * HOOK_RADIUS + PIVOT_Z;
export const LATCH_LENGTH = Math.hypot(SHUT_RISE, SHUT_BACK);
const SHUT_ANGLE = Math.atan2(SHUT_BACK, SHUT_RISE);
const OPEN_ANGLE = Math.PI - 0.12;

// Depths: the throats' plane, where the yarn runs through the hooks; a held loop's head behind the
// stem and open latch, and behind the shut hook as it rides over it; the fabric's legs, on its face
// toward the needles, and its heads and sinker loops beyond them.
const THROAT_Z = -HOOK_RADIUS;
const STEM_BACK_Z = -0.2;
const HOOK_BACK_Z = -(2 * HOOK_RADIUS + HOOK_WIRE + YARN_RADIUS + 0.02);
const FABRIC_LEGS = 0.2;
const FABRIC_HEADS = 0.46;
// How far a loop's head stays from the latch it rides, and from the spoon at the latch's end.
const LATCH_CLEAR = LATCH_RADIUS + YARN_RADIUS + 0.01;
const SPOON_RADIUS = LATCH_RADIUS * 1.8;
const SPOON_CLEAR = SPOON_RADIUS + YARN_RADIUS + 0.01;

// One course of fabric, and a loop from its head down to its sinker loops.
const COURSE = 0.6;
const LOOP = 1.05;
// A held loop's head sits at the verge, in its needle's throat at rest.
const HEAD = 0.1;
const REST = HEAD + HOOK_RADIUS;
// Risen to clear, the needle leaves the loop it holds resting on its open latch, short of the spoon.
const CLEAR = 0.85;
const KNOCK = -0.32;
// Where the sinkers hold the new yarn between needles: at the verge, above where the old loops are
// knocked over, so each new loop hangs from them down through its old loop into the hook.
const SINKER = HEAD + 0.05;
// Beside its hook, the yarn and a loop held on a needle clear the stem and hook to either side; the
// new loop's legs pass up through the old loop there once it is knocked over. Out of the hook, the
// yarn has already turned this far toward where its legs run.
const BESIDE = 0.13;
const HOOK_DIP = 0.08;
// How far a loop runs straight along the course into each of its sinker loops; and how much it still
// runs along the course where it turns toward the other half of a sinker loop at another height.
const SINKER_RUN = 0.4;
const SINKER_TURN = 0.05;
// How much further than the sinker the yarn to the carrier runs from a sinker loop that has come forward.
const ONWARD = 0.3;
// Drawn tight by its needle and the sinkers, the yarn runs this much straighter than a smooth curve
// between where it bears on them and on the old loop.
const TAUT = 0.5;
// How wide a loop on a needle opens to let the new yarn through it.
const RING = 0.27;
// Knocked over, an old loop lies round the new loop's legs where they come up through it, its head
// up the bed from them and behind them.
const KNOCKED = { y: HEAD, z: THROAT_Z - 0.17 };
const BELOW = 2;
const BULGE = 0.16;

// The carrier's eyelet reaches under the raised hooks, between their tips and their latches; its
// arm runs back behind the needles and up.
export const FEED_Y = CLEAR - 0.31;
const CARRIER_BACK = -0.55;
export const CARRIER_RADIUS = 0.035;

// The carriage starts and ends a pass this far from the middle: far enough for every needle to rise
// after the pass begins and to have been taken down before it ends.
const REACH = 4;

// Where in its cycle a needle is, in needle pitches the carriage has travelled since the carrier
// passed it: rising, dropping, back at rest, taken down.
const RISE: [number, number] = [-1.8, -0.8];
const DROP: [number, number] = [0.15, 0.95];
// By the end of its drop, a needle has pulled its new loop through the old one.
export const THROUGH = DROP[1];
const BACK: [number, number] = [1.05, 1.6];
const TAKE: [number, number] = [1.0, 1.9];
// As soon as it is off the hook, the old loop starts to turn over into the fabric round the new
// loop's legs. Its head, from where they pass through it, starts up and behind them, comes over the
// top as the hook rises behind them, and settles above and in front of them; the legs spread apart.
// Meanwhile the new loop's sinker loops come forward over the old loops and down in front of their
// legs, below the old loop's head.
const TURN: [number, number] = [DROP[1], DROP[1] + 0.35];
// Until the next needle has knocked over too, the sinker between them still holds the yarn, so the
// takedown draws a wale only part of the way down; it finishes once that sinker lets go.
const PARTLY = 0;
const HEAD_ANGLES = [-Math.PI / 4, 0.35, 0.68] as const;
const HEAD_REACH = [0.17, 0.26] as const;
const LEGS_APART = [BESIDE, 0.2] as const;

export type Point = { readonly x: number; readonly y: number; readonly z: number };

export type YarnPoint = Point & {
  readonly course: number;
  // The needle whose loop this is, or -1 where the yarn turns at an edge or runs to the carrier.
  readonly needle: number;
  // Where along its loop: 0 at the head, ±π at the sinker loops; NaN off a loop.
  readonly along: number;
  // How far a loop being formed is still drawn tight by its needle and sinkers: 1 tight, 0 relaxed.
  readonly taut: number;
  // How far a loop has turned over into the fabric round the loop below it: 0 while it is being
  // drawn through that loop on its needle, 1 once it lies in the fabric.
  readonly turned: number;
};

export type Needle = {
  readonly x: number;
  // Height of the top of the hook.
  readonly top: number;
  // 0 open, lying along the stem below its pivot; 1 shut across the hook's mouth.
  readonly latch: number;
  // Where in its knitting cycle: how far the carriage has come since the carrier passed it.
  readonly phase: number;
};

export type NeedleFrame = {
  // The course the carriage is knitting; it runs left to right on even passes.
  readonly pass: number;
  // How far through the pass, 0 to 1.
  readonly progress: number;
  readonly carriage: number;
  readonly needles: readonly Needle[];
  readonly yarn: readonly YarnPoint[];
  // Which step the middle needle is at.
  readonly step: number;
};

const smooth = ([from, to]: readonly [number, number], value: number) => {
  const t = Math.min(Math.max((value - from) / (to - from), 0), 1);
  return t * t * (3 - 2 * t);
};

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

const mixPoint = (a: Point, b: Point, t: number): Point => ({
  x: lerp(a.x, b.x, t),
  y: lerp(a.y, b.y, t),
  z: lerp(a.z, b.z, t),
});

const ORIGIN: Point = { x: 0, y: 0, z: 0 };

// A point moved along a direction, and the way from one point to another.
const offset = (point: Point, direction: Point, amount: number): Point => ({
  x: point.x + direction.x * amount,
  y: point.y + direction.y * amount,
  z: point.z + direction.z * amount,
});
const towards = (from: Point, to: Point): Point => ({ x: to.x - from.x, y: to.y - from.y, z: to.z - from.z });
const extent = ({ x, y, z }: Point) => Math.hypot(x, y, z);
// The way from one point to another, `size` long.
const heading = (from: Point, to: Point, size: number): Point => {
  const way = towards(from, to);
  return offset(ORIGIN, way, size / Math.max(extent(way), 1e-9));
};

// A smooth line from one point to another, leaving and arriving along the given directions, whose
// lengths shape how far it carries on that way: the points after `from`, up to and including `to`.
const hermiteAt = (from: Point, out: Point, to: Point, into: Point, t: number): Point => {
  const t2 = t * t;
  const t3 = t2 * t;
  const [a, b, c, d] = [2 * t3 - 3 * t2 + 1, t3 - 2 * t2 + t, 3 * t2 - 2 * t3, t3 - t2];
  const axis = (key: "x" | "y" | "z") => a * from[key] + b * out[key] + c * to[key] + d * into[key];
  return { x: axis("x"), y: axis("y"), z: axis("z") };
};
const hermite = (from: Point, out: Point, to: Point, into: Point, steps: number): Point[] =>
  Array.from({ length: steps }, (_, index) => hermiteAt(from, out, to, into, (index + 1) / steps));

export const NEEDLE_X = Array.from({ length: NEEDLES }, (_, index) => index - (NEEDLES - 1) / 2);

const hookAt = (phase: number) => {
  const raised = lerp(REST, CLEAR, smooth(RISE, phase));
  const dropped = lerp(raised, KNOCK, smooth(DROP, phase));
  return lerp(dropped, REST, smooth(BACK, phase));
};

// The phase at which a dropping hook's top passes a height.
const dropsTo = (height: number) => {
  let low = DROP[0];
  let high = DROP[1];
  for (let step = 0; step < 30; step += 1) {
    const middle = (low + high) / 2;
    if (hookAt(middle) > height) low = middle;
    else high = middle;
  }
  return (low + high) / 2;
};

// The old loop shuts the latch as it rides up the stem to the pivot, and leaves the needle once the
// top of the hook has dropped clear below it.
const LATCH_SHUT = dropsTo(HEAD + PIVOT_DROP);
const CAST_OFF = dropsTo(HEAD - HOOK_WIRE - YARN_RADIUS - 0.03);

// The fabric gives under the needle's pull. Dragged up the shut latch and over the hook, the old loop
// opens wider round them; then, pulled tight round the new loop's legs as the hook draws them down
// through it, it draws the fabric after it: the loops round its wale are drawn up toward its head, a
// course and more down, and those beside lean in to give it yarn. Once the needle has pulled through
// and rises back, the fabric springs back down, a little past where it lies, and settles.
const STRETCH = 0.1;
const TUG = 0.75;
const TUG_ACROSS = 0.6;
const TUG_DOWN = 0.9;
const SPRING = { damping: 4.05, frequency: 6.15 };
const tugAt = (phase: number) => {
  const since = phase - (DROP[1] + 0.05);
  if (since <= 0) return smooth([LATCH_SHUT - 0.15, DROP[1]], phase);
  const { damping, frequency } = SPRING;
  return (
    Math.exp(-damping * since) * (Math.cos(frequency * since) + (damping / frequency) * Math.sin(frequency * since))
  );
};

// Where along its cycle a needle's latch is, as the loop it holds moves along it: `along` is the
// loop's height less the hook's top.
const latchAt = (phase: number, along: number) => {
  if (phase < 0) return 1 - smooth([-HOOK_RADIUS - 0.03, -0.28], along);
  if (phase < CAST_OFF) return smooth([-PIVOT_DROP - LATCH_LENGTH, -PIVOT_DROP - 0.05], along);
  return 1;
};

const latchAngle = (latch: number) => lerp(OPEN_ANGLE, SHUT_ANGLE, latch);

// How far back a loop's head at a height must stay to keep behind the latch, and behind its spoon as
// the latch swings shut past it, easing forward once the spoon is above it: no further forward than
// the back of the stem.
const behindLatch = (top: number, latch: number, height: number) => {
  const angle = latchAngle(latch);
  let z = STEM_BACK_Z;
  for (let step = 0; step <= 12; step += 1) {
    const along = (LATCH_LENGTH * step) / 12;
    const clear = step === 12 ? SPOON_CLEAR : LATCH_CLEAR;
    const back = PIVOT_Z - along * Math.sin(angle);
    const rise = height - (top - PIVOT_DROP + along * Math.cos(angle));
    if (Math.abs(rise) < clear) z = Math.min(z, back - Math.sqrt(clear ** 2 - rise ** 2));
    else if (step === 12 && rise < 0) z = Math.min(z, lerp(back, STEM_BACK_Z, smooth([-clear, -clear - 0.15], rise)));
  }
  return z;
};

// The loop a needle held when the pass began: its head in the throat at rest, round the back of the
// stem once the needle has risen through it, then behind the shut latch and hook as it rides up
// them, and once over the top, knocked over behind the new loop's legs.
const oldHeadAt = (phase: number, top: number, latch: number) => {
  const along = HEAD - top;
  if (phase < 0) {
    const z = lerp(THROAT_Z, STEM_BACK_Z, smooth([-HOOK_RADIUS - 0.03, -0.3], along));
    return { y: HEAD, z: along < -0.3 ? Math.min(z, behindLatch(top, latch, HEAD)) : z };
  }
  if (phase < CAST_OFF) {
    if (along > -HOOK_RADIUS - TIP) return { y: HEAD, z: HOOK_BACK_Z };
    return { y: HEAD, z: behindLatch(top, latch, HEAD) };
  }
  const off = smooth([CAST_OFF, DROP[1]], phase);
  return { y: lerp(HEAD, KNOCKED.y, off), z: lerp(HOOK_BACK_Z, KNOCKED.z, off) };
};

// As a dropping hook brings the new yarn down to the loop the needle holds, that loop opens wide;
// then the yarn either side of the hook passes down through it.
const openedAt = (phase: number, top: number) => (phase > 0 ? 1 - smooth([HEAD + 0.25, HEAD + 0.4], top) : 0);
const threadedAt = (phase: number, top: number) =>
  phase >= CAST_OFF ? 1 : phase > 0 ? 1 - smooth([HEAD + 0.08, HEAD + 0.26], top) : 0;

// When a needle's throat comes down onto the yarn, which runs from the needle before it, already
// dropping, to the carrier's eyelet: when it takes the yarn.
const CATCH = (() => {
  for (let phase = 0; phase < DROP[0] + 0.3; phase += 0.002) {
    const before = hookAt(phase + 1) - HOOK_RADIUS;
    const reaches = before + (FEED_Y - before) / (1 + phase);
    if (hookAt(phase) - HOOK_RADIUS <= reaches) return phase;
  }
  return DROP[0] + 0.3;
})();

// Where the middle needle's cycle starts each step: rising, taking the yarn, shutting the latch on
// it, and pulling it through the old loop as that comes off.
const STEP_PHASES = [RISE[0], 0, CATCH + 0.06, CAST_OFF] as const;

// One loop of `needle` between two sinker points, its head and sinker loops in one plane and its legs
// in the fabric's face, as plain jersey lies. A flat loop lies straight.
const jersey = (needle: number, head: Point, sinkLeft: Point, sinkRight: Point): Point[] => {
  const x = NEEDLE_X[needle];
  const open = Math.min(1, Math.abs(head.y - (sinkLeft.y + sinkRight.y) / 2) / 0.5);
  return Array.from({ length: 33 }, (_, step) => {
    const t = -Math.PI + (2 * Math.PI * step) / 32;
    const s = (t + Math.PI) / (2 * Math.PI);
    const sinkY = lerp(sinkLeft.y, sinkRight.y, s);
    const sinkZ = lerp(sinkLeft.z, sinkRight.z, s);
    const back = lerp(head.z, sinkZ, (1 - Math.cos(t)) / 2);
    return {
      x: x + t / (2 * Math.PI) + BULGE * open * Math.sin(2 * t),
      y: sinkY + ((head.y - sinkY) * (1 + Math.cos(t))) / 2,
      z: back + ((FABRIC_LEGS - back) * open * (1 - Math.cos(2 * t))) / 2,
    };
  });
};

// A smooth line through the points, the ends held, `steps` samples a span. Through a loop's seven
// points (sinker, leg, beside its head, head, beside, leg, sinker) it takes the same 33 samples as a
// jersey loop, so the two can blend.
const LOOP_STEPS = [8, 4, 4, 4, 4, 8];
const through = (
  points: readonly Point[],
  steps: readonly number[] = LOOP_STEPS,
  // the way the line leaves its first point and arrives at its last, if not straight at its neighbour
  start?: Point,
  end?: Point,
  // how much straighter than a Catmull-Rom spline it runs between its points: 0 none, 1 straight
  tension = 0,
): Point[] => {
  const last = points.length - 1;
  const at = (index: number): Point => {
    if (index < 0) return start ? offset(points[1], start, -2) : points[0];
    if (index > last) return end ? offset(points[last - 1], end, 2) : points[last];
    return points[index];
  };
  const result: Point[] = [];
  for (let span = 0; span < last; span += 1) {
    const [p0, p1, p2, p3] = [at(span - 1), at(span), at(span + 1), at(span + 2)];
    const out = offset(ORIGIN, towards(p0, p2), (1 - tension) / 2);
    const into = offset(ORIGIN, towards(p1, p3), (1 - tension) / 2);
    for (let step = 0; step < steps[span]; step += 1) result.push(hermiteAt(p1, out, p2, into, step / steps[span]));
  }
  result.push(points[last]);
  return result;
};

const tag = (
  points: readonly Point[],
  course: number,
  needle: number,
  taut: (step: number) => number = () => 0,
  turned = 1,
): YarnPoint[] =>
  points.map((point, step) => ({
    x: point.x,
    y: point.y,
    z: point.z,
    course,
    needle,
    along: -Math.PI + (2 * Math.PI * step) / 32,
    taut: taut(step),
    turned,
  }));

// How much of a loop on a needle goes with it: the part in its hook or round its stem, and less of
// the yarn either side down to where its legs start; its legs and sinker loops hang with the fabric,
// so a new loop gathered over an old loop's head stays over it.
const hookWeight = (step: number) => {
  const along = Math.abs(-Math.PI + (2 * Math.PI * step) / 32);
  return 1 - smooth([Math.PI / 4, Math.PI / 2], along);
};
// How far an old loop gives with the fabric under its needle's pull: its legs and sinker loops, not
// its head, on the needle or round the new loop's legs.
const fabricWeight = (step: number) =>
  smooth([Math.PI / 4, (3 * Math.PI) / 4], Math.abs(-Math.PI + (2 * Math.PI * step) / 32));

// The needles' tilt: from a point laid out with the needles upright to where it lies with them
// sloping up toward the fabric's face, turned about the verge.
const TILT = Math.PI / 4;
const tilted = ({ x, y, z }: Point): Point => ({
  x,
  y: y * Math.cos(TILT) - z * Math.sin(TILT),
  z: y * Math.sin(TILT) + z * Math.cos(TILT),
});

// Where a point laid out with the needles upright lies, as far as it goes with them: 1 tilted with
// the needles, 0 hanging with the fabric.
const place = (point: Point, onNeedle: number): Point => mixPoint(point, tilted(point), onNeedle);

// Where to lay out a point so that, placed, it lies at `point`.
const unplace = ({ x, y, z }: Point, onNeedle: number): Point => {
  const along = 1 - onNeedle + onNeedle * Math.cos(TILT);
  const across = onNeedle * Math.sin(TILT);
  const scale = along * along + across * across;
  return { x, y: (along * y + across * z) / scale, z: (along * z - across * y) / scale };
};

// Where the new loop's legs pass through the old loop: knocked over, in the throats' plane at the
// verge; pushed forward over the top of the needle while it is still down, so it can rise behind the
// old loop; and settled into the fabric a course down, the old loop's head above and in front of them.
const NECK_KNOCKED = tilted({ x: 0, y: HEAD, z: THROAT_Z });
const NECK_FORWARD = tilted({ x: 0, y: HEAD, z: 0.25 });
const NECK_SETTLED: Point = {
  x: 0,
  y: HEAD - COURSE - HEAD_REACH[1] * Math.cos(HEAD_ANGLES[2]),
  z: FABRIC_HEADS - HEAD_REACH[1] * Math.sin(HEAD_ANGLES[2]),
};

const joined = (parts: YarnPoint[][]) => parts.flatMap((part, index) => (index ? part.slice(1) : part));

// The yarn turning at the edge of the fabric from the end of one course into the start of the next:
// on out the way it was going, round, and back in the way the next course starts.
const SELVEDGE_RUN = 0.8;
const selvedge = (before: Point, from: YarnPoint, to: YarnPoint, after: Point): YarnPoint[] =>
  hermite(from, heading(before, from, SELVEDGE_RUN), to, heading(to, after, SELVEDGE_RUN), 32)
    .slice(0, -1)
    .map((point) => ({ ...point, course: to.course, needle: -1, along: Number.NaN, taut: 0, turned: 1 }));

export const passDirection = (pass: number): 1 | -1 => (pass % 2 === 0 ? 1 : -1);

export type Tube = { readonly points: readonly Point[]; readonly radius: number };

// A needle as tubes: its stem, its hook curling back over the throat to its tip, and its latch with
// the spoon at its end.
export const needleTubes = ({ x, top, latch }: Needle): Tube[] => {
  const stem = Array.from({ length: 18 }, (_, index) => ({
    x,
    y: top - HOOK_RADIUS - (STEM_LENGTH * (17 - index)) / 17,
    z: 0,
  }));
  const curl = Array.from({ length: 13 }, (_, index) => {
    const angle = (Math.PI * index) / 12;
    return {
      x,
      y: top - HOOK_RADIUS + HOOK_RADIUS * Math.sin(angle),
      z: -HOOK_RADIUS + HOOK_RADIUS * Math.cos(angle),
    };
  });
  curl.push({ x, y: top - HOOK_RADIUS - TIP, z: -2 * HOOK_RADIUS });
  const angle = latchAngle(latch);
  const along = (length: number): Point => ({
    x,
    y: top - PIVOT_DROP + length * Math.cos(angle),
    z: PIVOT_Z - length * Math.sin(angle),
  });
  return [
    { points: stem, radius: STEM_RADIUS },
    { points: curl, radius: HOOK_WIRE },
    {
      points: Array.from({ length: 7 }, (_, index) => along(((LATCH_LENGTH - 0.08) * index) / 6)),
      radius: LATCH_RADIUS,
    },
    { points: [along(LATCH_LENGTH - 0.09), along(LATCH_LENGTH)], radius: SPOON_RADIUS },
  ].map((tube) => ({ ...tube, points: tube.points.map(tilted) }));
};

// The carrier: the eyelet under the hooks, its arm running back behind the needles and up.
export const carrierTubes = (carriage: number): Tube[] => {
  const back = tilted({ x: carriage, y: FEED_Y, z: CARRIER_BACK });
  return [
    {
      points: [
        tilted({ x: carriage, y: FEED_Y, z: THROAT_Z - YARN_RADIUS - CARRIER_RADIUS }),
        back,
        { ...back, y: back.y + 2 },
      ],
      radius: CARRIER_RADIUS,
    },
  ];
};

// The yarn eased into a smooth curve: each point drawn toward its neighbours along the yarn, nearer
// ones more, so that no bend is tighter than the yarn can make without creasing, and the joins
// between the pieces it is built from run smoothly; the ends stay put.
const EASE = 0.035;
const eased = (yarn: readonly YarnPoint[]): YarnPoint[] => {
  const arc = [0];
  for (let index = 1; index < yarn.length; index += 1)
    arc.push(arc[index - 1] + extent(towards(yarn[index - 1], yarn[index])));
  const total = arc[arc.length - 1];
  const reach = 3 * EASE;
  return yarn.map((point, index) => {
    let [x, y, z, sum] = [0, 0, 0, 0];
    for (let other = index; other >= 0 && arc[index] - arc[other] <= reach; other -= 1) {
      const share = (arc[Math.min(other + 1, yarn.length - 1)] - arc[Math.max(other - 1, 0)]) / 2 || 1e-6;
      const weight = Math.exp(-((arc[index] - arc[other]) ** 2) / (2 * EASE * EASE)) * share;
      [x, y, z, sum] = [
        x + yarn[other].x * weight,
        y + yarn[other].y * weight,
        z + yarn[other].z * weight,
        sum + weight,
      ];
    }
    for (let other = index + 1; other < yarn.length && arc[other] - arc[index] <= reach; other += 1) {
      const share = (arc[Math.min(other + 1, yarn.length - 1)] - arc[other - 1]) / 2 || 1e-6;
      const weight = Math.exp(-((arc[other] - arc[index]) ** 2) / (2 * EASE * EASE)) * share;
      [x, y, z, sum] = [
        x + yarn[other].x * weight,
        y + yarn[other].y * weight,
        z + yarn[other].z * weight,
        sum + weight,
      ];
    }
    const hold = Math.min(1, Math.min(arc[index], total - arc[index]) / reach);
    return { ...point, ...mixPoint(point, { x: x / sum, y: y / sum, z: z / sum }, hold) };
  });
};

// The yarn drawn as a smooth curve through its points: each stretch between two of them split into
// pieces along a centripetal Catmull-Rom spline, which takes tight turns without looping or
// overshooting. Points in between take their tags from the stretch they are on.
const SMOOTH_PIECES = 3;
const subdivided = (yarn: readonly YarnPoint[]): YarnPoint[] => {
  const knot = (a: Point, b: Point) => Math.sqrt(Math.max(extent(towards(a, b)), 1e-6));
  const result: YarnPoint[] = [];
  for (let index = 0; index + 1 < yarn.length; index += 1) {
    const [p0, p1, p2, p3] = [
      yarn[Math.max(index - 1, 0)],
      yarn[index],
      yarn[index + 1],
      yarn[Math.min(index + 2, yarn.length - 1)],
    ];
    const t1 = knot(p0, p1);
    const t2 = t1 + knot(p1, p2);
    const t3 = t2 + knot(p2, p3);
    const sameLoop = p1.course === p2.course && p1.needle === p2.needle && Number.isFinite(p1.along + p2.along);
    result.push(p1);
    for (let piece = 1; piece < SMOOTH_PIECES; piece += 1) {
      const share = piece / SMOOTH_PIECES;
      const t = lerp(t1, t2, share);
      const at = (a: Point, b: Point, from: number, to: number) => mixPoint(a, b, (t - from) / (to - from));
      const a1 = at(p0, p1, 0, t1);
      const a2 = at(p1, p2, t1, t2);
      const a3 = at(p2, p3, t2, t3);
      const point = at(at(a1, a2, 0, t2), at(a2, a3, t1, t3), t1, t2);
      result.push({
        ...(share < 0.5 ? p1 : p2),
        ...point,
        along: sameLoop ? lerp(p1.along, p2.along, share) : (share < 0.5 ? p1 : p2).along,
        taut: lerp(p1.taut, p2.taut, share),
        turned: lerp(p1.turned, p2.turned, share),
      });
    }
  }
  if (yarn.length) result.push(yarn[yarn.length - 1]);
  // a tube cannot be drawn along a stretch of no length
  const kept: YarnPoint[] = [];
  result.forEach((point) => {
    if (!kept.length || extent(towards(kept[kept.length - 1], point)) > 0.004) kept.push(point);
  });
  return kept;
};

const blend = (from: readonly Point[], to: readonly Point[], t: number) =>
  t <= 0 ? [...from] : t >= 1 ? [...to] : from.map((point, index) => mixPoint(point, to[index], t));

export const needleFrame = (seconds: number, { untangled = true, smoothing = true } = {}): NeedleFrame => {
  const pass = Math.floor(seconds / PASS_SECONDS);
  const progress = seconds / PASS_SECONDS - pass;
  const direction = passDirection(pass);
  const carriage = direction * lerp(-REACH, REACH, progress);
  const phases = NEEDLE_X.map((x) => direction * (carriage - x));
  const tops = phases.map(hookAt);
  const taken = phases.map((phase, needle) => {
    const next = needle + direction;
    const freed = next >= 0 && next < NEEDLES ? smooth([DROP[1], DROP[1] + 0.9], phases[next]) : 1;
    return smooth(TAKE, phase) * lerp(PARTLY, 1, freed);
  });
  // The takedown as it reaches across the fabric: each needle's wale, and in between.
  const takenAt = (x: number) => {
    const at = Math.min(Math.max(x - NEEDLE_X[0], 0), NEEDLES - 1);
    const below = Math.min(Math.floor(at), NEEDLES - 2);
    return lerp(taken[below], taken[below + 1], at - below);
  };
  const latches = phases.map((phase, needle) => latchAt(phase, HEAD - tops[needle]));
  const throat = (needle: number): Point => ({ x: NEEDLE_X[needle], y: tops[needle] - HOOK_RADIUS, z: THROAT_Z });
  // Where each needle's new loop passes through its old loop, and where the old loop's head is: on
  // the needle until it is knocked over, then turning over into the fabric round the new loop's legs.
  const necks = NEEDLE_X.map((x, needle) => {
    const forward = mixPoint(NECK_KNOCKED, NECK_FORWARD, smooth(TURN, phases[needle]));
    return { ...mixPoint(forward, NECK_SETTLED, taken[needle]), x };
  });
  const neckAt = (needle: number, side: number) => ({
    ...necks[needle],
    x: necks[needle].x + side * lerp(LEGS_APART[0], LEGS_APART[1], taken[needle]),
  });
  const heads = phases.map((phase, needle) => {
    const x = NEEDLE_X[needle];
    if (phase < DROP[1]) return tilted({ x, ...oldHeadAt(phase, tops[needle], latches[needle]) });
    const take = taken[needle];
    const angle = lerp(HEAD_ANGLES[0], HEAD_ANGLES[1], smooth(TURN, phase)) + (HEAD_ANGLES[2] - HEAD_ANGLES[1]) * take;
    const reach = lerp(HEAD_REACH[0], HEAD_REACH[1], take);
    return { x, y: necks[needle].y + reach * Math.cos(angle), z: necks[needle].z + reach * Math.sin(angle) };
  });
  // The fabric as it gives under the needles' pulls, drawn toward the heads of the old loops they are
  // pulling through, as far as a point is `free` to move.
  const tugs = phases.map(tugAt);
  const tugged = (point: Point, free = 1): Point => {
    let { x, y, z } = point;
    tugs.forEach((pull, needle) => {
      const head = heads[needle];
      const across = (point.x - head.x) / TUG_ACROSS;
      const down = Math.max(head.y - point.y, 0) / TUG_DOWN;
      const share = free * TUG * pull * Math.exp(-across * across - down * down);
      x += (head.x - point.x) * share;
      y += (head.y - point.y) * share;
      z += (head.z - point.z) * share;
    });
    return { ...point, x, y, z };
  };

  // The courses knitted before this pass, oldest first; the newest still sits on the needles. The
  // takedown draws the fabric down a course, wale by wale as each needle's cycle ends.
  const knitted = Array.from({ length: BELOW + 1 }, (_, index) => {
    const below = BELOW - index;
    const course = pass - 1 - below;
    const stitches = NEEDLE_X.map((x, needle) => {
      if (below) {
        const sink = (side: number): Point => ({ x: x + side / 2, y: HEAD - LOOP - COURSE * below, z: FABRIC_HEADS });
        return jersey(needle, { x, y: HEAD - COURSE * below, z: FABRIC_HEADS }, sink(-1), sink(1)).map((point) =>
          tugged({ ...point, y: point.y - COURSE * takenAt(point.x) }),
        );
      }
      // The loop on the needle: once the needle has risen through it, it hugs the stem, opening wide
      // as the hook brings the new yarn down to it; off the hook, it lies round the new loop's legs,
      // its legs and sinker loops in the fabric, and its head goes with the needle until it has turned
      // over into the fabric.
      const phase = phases[needle];
      const holding = 1 - smooth(TURN, phase);
      const sink = (side: number): Point => ({
        x: x + side / 2,
        y: HEAD - LOOP - COURSE * takenAt(x + side / 2),
        z: FABRIC_HEADS,
      });
      const head = unplace(heads[needle], holding);
      const loose = jersey(needle, head, sink(-1), sink(1));
      const along = HEAD - tops[needle];
      const hugging =
        phase < 0
          ? smooth([-HOOK_RADIUS - 0.03, -0.3], along)
          : phase < CAST_OFF
            ? 1
            : 1 - smooth([CAST_OFF, DROP[1]], phase);
      // Only the head changes: the legs and sinker loops lie as in the fabric. Opened, its sides stand
      // clear of the yarn leaving the hook and behind it.
      const opened = openedAt(phase, tops[needle]);
      const wide = lerp(BESIDE, RING + STRETCH * tugs[needle], opened);
      const side = { y: head.y - lerp(0.04, 0.08, opened), z: lerp(head.z / 2 + 0.07, THROAT_Z - 0.04, opened) };
      // round the back of the latch or the hook, clear of it, on the way from its side to its head
      const back = { y: lerp(side.y, head.y, Math.SQRT1_2), z: lerp(side.z, head.z, Math.SQRT1_2) };
      const crown = through(
        [
          loose[6],
          loose[8],
          { x: x - wide, ...side },
          { x: x - Math.SQRT1_2 * wide, ...back },
          head,
          { x: x + Math.SQRT1_2 * wide, ...back },
          { x: x + wide, ...side },
          loose[24],
          loose[26],
        ],
        [0, 4, 2, 2, 2, 2, 4, 0],
      ).slice(0, 16);
      const hug = [...loose.slice(0, 8), ...crown, ...loose.slice(24)];
      return blend(loose, hug, hugging).map((point, step) =>
        tugged(place(point, holding * hookWeight(step)), fabricWeight(step)),
      );
    });
    const strand = joined(stitches.map((points, needle) => tag(points, course, needle)));
    return passDirection(course) === 1 ? strand : strand.reverse();
  });
  const yarn: YarnPoint[] = [];
  knitted.forEach((strand) => {
    if (yarn.length) yarn.push(...selvedge(yarn[yarn.length - 2], yarn[yarn.length - 1], strand[0], strand[1]));
    yarn.push(...strand);
  });

  // The course being knitted. Between two needles that have the yarn it hangs straight in the
  // throats' plane until the sinker between them holds it at the verge. As the needles either side
  // turn their old loops over, the sinker loop between them comes forward over the old loops and down
  // in front of their legs, below the old loops' heads.
  const order = NEEDLE_X.map((_, index) => (direction === 1 ? index : NEEDLES - 1 - index));
  const held = (x: number, height: number) => tilted({ x, y: height, z: THROAT_Z });
  const between = (a: number, b: number) =>
    held((NEEDLE_X[a] + NEEDLE_X[b]) / 2, Math.max(SINKER, (throat(a).y + throat(b).y) / 2));
  const edge = (needle: number) => held(NEEDLE_X[needle] - direction / 2, SINKER);
  // From the needle that took the yarn last, it runs over the next sinker, which holds it once the
  // carrier is past, to the eyelet. As that sinker loop comes forward (`onward` from 0 to 1), the yarn
  // leaves it for a point further on toward the next needle, rather than stepping back up to the sinker.
  const toCarrier = (needle: number, onward = 0) => {
    const phase = phases[needle];
    const reach = Math.min(0.5 + ONWARD * onward, phase);
    const hook = throat(needle);
    const height = hook.y + ((FEED_Y - hook.y) * reach) / phase;
    return held(NEEDLE_X[needle] + direction * reach, phase > 0.5 ? Math.max(SINKER, height) : height);
  };
  // A sinker loop moves as one piece. As the first needle either side of it turns its old loop over, it
  // comes forward over the old loops and a little way down, clear of that loop's head; once the second
  // has turned over too, it goes on down in front of their legs. Both ends keep below the head of the
  // first one's old loop, which the takedown draws down first, so the loop goes down level.
  // `other` is the needle sharing it, -1 while that needle has yet to take the yarn.
  const settled = (sinker: Point, needle: number, other = needle): Point => {
    const shared = other >= 0 && other !== needle;
    const [early, late] = shared && phases[other] > phases[needle] ? [other, needle] : [needle, other];
    const first = smooth(TURN, phases[early]);
    const second = other < 0 ? 0 : smooth(TURN, phases[shared ? late : needle]);
    const depth = (LOOP - COURSE) * lerp(0.3, 1, second);
    return {
      x: sinker.x,
      y: lerp(sinker.y, heads[early].y - depth, smooth([0.3, 1], first)),
      z: lerp(sinker.z, FABRIC_HEADS, smooth([0, 0.5], first)),
    };
  };
  const eyelet: Point = { x: carriage, y: FEED_Y, z: THROAT_Z };

  // A needle the carrier has passed takes the yarn once its throat comes down onto the yarn, which
  // runs from the last needle to take it, or the sinker at the fabric's edge, to the eyelet.
  const laid: number[] = [];
  let from = { x: NEEDLE_X[order[0]] - direction / 2, y: SINKER };
  for (const needle of order) {
    if (phases[needle] <= 0) break;
    const reaches = from.y + ((eyelet.y - from.y) * (NEEDLE_X[needle] - from.x)) / (eyelet.x - from.x);
    if (phases[needle] < DROP[0] + 0.3 && throat(needle).y > reaches) break;
    laid.push(needle);
    from = throat(needle);
  }

  // Each needle's halves of the sinker loops either side of it.
  const lastLaid = laid[laid.length - 1];
  const nextInBed = laid.length > 0 && lastLaid + direction >= 0 && lastLaid + direction < NEEDLES;
  const halves = laid.map((needle, index) => ({
    before: index ? settled(between(laid[index - 1], needle), needle, laid[index - 1]) : settled(edge(needle), needle),
    after:
      index + 1 < laid.length
        ? settled(between(needle, laid[index + 1]), needle, laid[index + 1])
        : settled(toCarrier(needle), needle, nextInBed ? -1 : needle),
  }));
  const over = laid.length
    ? toCarrier(lastLaid, nextInBed ? smooth([0, 0.5], smooth(TURN, phases[lastLaid])) : 0)
    : null;
  const loops = laid.map((needle, index) => {
    const x = NEEDLE_X[needle];
    const take = taken[needle];
    const { before, after } = halves[index];
    const [sinkLeft, sinkRight] = direction === 1 ? [before, after] : [after, before];
    const hook = throat(needle);
    // Once the hook has brought the yarn down through the old loop, each leg passes up through that
    // loop to the sinkers. The yarn leaves the hook to either side, clear of the stem and hook, already
    // turning the way that leg runs: a U round the hook, opening toward the legs.
    const tip = tilted(hook);
    const threaded = threadedAt(phases[needle], tops[needle]);
    const pinch = (sink: Point, side: number, beside: Point) =>
      mixPoint(mixPoint(sink, beside, 0.5), neckAt(needle, side), threaded);
    const beside = (sink: Point, side: number): Point => {
      const leg = towards(tip, pinch(sink, side, tilted({ x: x + side * BESIDE, y: hook.y - 0.03, z: THROAT_Z })));
      // turning less while the leg runs nearly level with the hook, so the U turns over smoothly
      const across = Math.max(Math.hypot(leg.y, leg.z), 0.3);
      return { x: x + side * BESIDE, y: tip.y + (HOOK_DIP * leg.y) / across, z: tip.z + (HOOK_DIP * leg.z) / across };
    };
    const [besideLeft, besideRight] = [beside(sinkLeft, -1), beside(sinkRight, 1)];
    const [pinchLeft, pinchRight] = [pinch(sinkLeft, -1, besideLeft), pinch(sinkRight, 1, besideRight)];
    // and at either end it runs along the course into the sinker loop it shares with the loop beside
    const level = (from: Point, to: Point): Point => ({ x: SINKER_RUN * extent(towards(from, to)), y: 0, z: 0 });
    const taut = through(
      [sinkLeft, pinchLeft, besideLeft, tip, besideRight, pinchRight, sinkRight],
      LOOP_STEPS,
      level(sinkLeft, pinchLeft),
      level(pinchRight, sinkRight),
      TAUT,
    );
    const relaxed = jersey(needle, hook, sinkLeft, sinkRight).map((point, step) => place(point, hookWeight(step)));
    const formed = taut.map((point, step) => mixPoint(point, relaxed[step], take));
    // Where the needle beside has its half of the sinker loop somewhere else, the yarn turns toward it
    // at the end of this one, so the two halves join in one smooth sweep.
    const otherBefore = index ? halves[index - 1].after : before;
    const otherAfter = index + 1 < laid.length ? halves[index + 1].before : after;
    const [otherLeft, otherRight] = direction === 1 ? [otherBefore, otherAfter] : [otherAfter, otherBefore];
    const turnEnd = (end: number, inner: number, away: Point) => {
      const size = extent(towards(formed[end], formed[inner]));
      const bend = heading(ORIGIN, { x: SINKER_TURN + away.x, y: away.y, z: away.z }, size);
      const inward = offset(ORIGIN, towards(formed[inner - 1], formed[inner + 1]), 2);
      const curve =
        end < inner
          ? hermite(formed[end], bend, formed[inner], inward, inner - end)
          : hermite(formed[inner], inward, formed[end], bend, end - inner);
      curve.forEach((point, step) => (formed[Math.min(end, inner) + step + 1] = point));
    };
    turnEnd(0, 4, towards(otherLeft, sinkLeft));
    turnEnd(32, 28, towards(sinkRight, otherRight));
    const tagged = tag(formed, pass, needle, () => 1 - take, smooth(TURN, phases[needle]));
    return direction === 1 ? tagged : tagged.reverse();
  });
  // From each loop's half of a sinker loop to the next one's: where one needle has let its half go
  // down and the next still holds the yarn at the verge, the yarn runs back up between them; and from
  // the last one up over the next sinker.
  const live: YarnPoint[] = [];
  const bridge = (to: Point) => {
    const last = live[live.length - 1];
    const gap = extent(towards(last, to));
    if (gap <= 0.02) return false;
    // pulled straight from one half to the other
    for (let step = 1; step < 16; step += 1) live.push({ ...last, ...mixPoint(last, to, step / 16) });
    return true;
  };
  loops.forEach((loop) => {
    if (!live.length) live.push(...loop);
    else live.push(...(bridge(loop[0]) ? loop : loop.slice(1)));
  });
  if (over && bridge(over)) live.push({ ...live[live.length - 1], ...over });
  const start = live.length
    ? live[0]
    : { ...edge(order[0]), course: pass, needle: -1, along: Number.NaN, taut: 0, turned: 0 };
  yarn.push(
    ...selvedge(yarn[yarn.length - 2], yarn[yarn.length - 1], start, live.length > 1 ? live[1] : tilted(eyelet)),
    ...live,
  );
  // On to the eyelet in short steps. This yarn is on its way to the next needle, where it will be
  // that needle's new loop: it already has to pass in front of the legs of the loop that needle holds.
  const last = yarn[yarn.length - 1];
  const next = order.find((needle) => !laid.includes(needle)) ?? -1;
  const reach = extent(towards(last, tilted(eyelet)));
  hermite(
    last,
    heading(yarn[yarn.length - 2], last, 0.6 * reach),
    tilted(eyelet),
    heading(last, tilted(eyelet), reach),
    36,
  ).forEach((point) =>
    yarn.push({
      ...point,
      course: pass,
      needle: next,
      along: next < 0 ? Number.NaN : -direction * 1.4,
      taut: 1,
      turned: 0,
    }),
  );

  const needles = NEEDLE_X.map((x, needle) => ({
    x,
    top: tops[needle],
    latch: latches[needle],
    phase: phases[needle],
  }));
  const featured = phases[FEATURED];
  const step = STEP_PHASES.reduce<number>((current, begin, index) => (featured >= begin ? index : current), 0);
  const obstacles = [...needles.flatMap(needleTubes), ...carrierTubes(carriage)];
  const drawn = untangled ? separate(yarn, YARN_RADIUS) : yarn;

  return {
    pass,
    progress,
    carriage,
    needles,
    yarn: smoothing ? subdivided(clearOf(eased(drawn), obstacles, YARN_RADIUS)) : drawn,
    step,
  };
};

const STEP_POSES = [RISE[1] - 0.05, CATCH + 0.02, Math.max(LATCH_SHUT, STEP_PHASES[2]) + 0.04, DROP[1]] as const;

// The second at which the middle needle shows a step, in the given pass.
export const stepSeconds = (pass: number, step: number) => {
  // how far the carriage has come from where the pass starts, when the needle reaches that pose
  const travelled = NEEDLE_X[FEATURED] * passDirection(pass) + STEP_POSES[step];
  return (pass + (travelled + REACH) / (2 * REACH)) * PASS_SECONDS;
};
